import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const mosaicTile: TemplateRegistration = {
  meta: {
    id: 'mosaic-tile',
    name: 'Mosaic Tile',
    category: 'Modern',
    description: 'Tiled mosaic layout with alternating color blocks',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Archivo', 'Outfit']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-light">
        <div class="container">
          <div class="tile-accent-bar"></div>
          <h2 class="section-title">About Us</h2>
          <p class="about-text">${safeAbout}</p>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map((s, i) => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        const spanRows = i % 3 === 0 ? 'tile-tall' : '';
        const accentClass = i % 2 === 0 ? 'tile-teal' : 'tile-amber';
        return `
          <div class="service-tile ${spanRows} ${accentClass}">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-mosaic">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map((url, i) => {
        const spanClass = i === 0 ? 'gallery-wide' : '';
        return `<div class="gallery-item ${spanClass}"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-light">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-mosaic">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map((t, i) => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        const accentClass = i % 2 === 0 ? 'tile-teal' : 'tile-amber';
        return `
          <div class="testimonial-card ${accentClass}">
            <div class="testimonial-body">
              <p>&ldquo;${tContent}&rdquo;</p>
            </div>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-alt">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-grid">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-light">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-light">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-alt">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-grid">
            <div class="contact-info">
              ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #0D9488; --bg: #FAFAF9; --bg-alt: #F5F5F4; --accent: #F59E0B;
      --text: #1C1917; --text-muted: #57534E;
      --font-heading: 'Archivo', sans-serif; --font-body: 'Outfit', sans-serif;
      --teal: #0D9488; --amber: #F59E0B; --coral: #F87171; --golden: #FBBF24; --lavender: #A78BFA;
      --shadow-sm: 0 1px 3px rgba(0,0,0,.08); --shadow-md: 0 4px 12px rgba(0,0,0,.1);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.6; }
    .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--primary); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* TILE ACCENT BAR */
    .tile-accent-bar { width: 60px; height: 4px; background: var(--teal); margin-bottom: 20px; border-radius: 2px; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(250,250,249,.95); backdrop-filter: blur(10px); border-bottom: 1px solid #E7E5E4; box-shadow: var(--shadow-sm); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; max-width: 1200px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.4rem; color: var(--text); font-weight: 800; }
    .nav-links { display: flex; gap: 24px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 600; }
    .nav-links a:hover { color: var(--primary); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 26px; height: 3px; background: var(--primary); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO - asymmetric mosaic tile grid */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; background: var(--bg); padding-top: 80px; }
    .hero-grid { display: grid; grid-template-columns: 2fr 1fr; grid-template-rows: auto auto; gap: 16px; max-width: 1200px; margin: 0 auto; padding: 40px 24px 80px; width: 100%; }
    /* Main tile - headline + CTA (spans 2 rows in col 1) */
    .hero-tile-main { grid-column: 1; grid-row: 1 / 3; background: var(--teal); color: #fff; border-radius: 16px; padding: 56px 40px; display: flex; flex-direction: column; justify-content: center; box-shadow: var(--shadow-md); }
    .hero-tile-main h1 { font-family: var(--font-heading); font-size: clamp(2.4rem, 6vw, 4.2rem); line-height: 1.1; margin-bottom: 24px; font-weight: 900; color: #fff; }
    .hero-cta { display: inline-block; padding: 14px 36px; background: #fff; color: var(--teal); font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: var(--shadow-md); align-self: flex-start; }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,.15); opacity: 1; color: var(--teal); }
    /* Subtitle tile */
    .hero-tile-sub { background: var(--coral); color: #fff; border-radius: 16px; padding: 32px 28px; display: flex; align-items: center; box-shadow: var(--shadow-md); }
    .hero-tile-sub p { font-size: clamp(.95rem, 1.8vw, 1.1rem); line-height: 1.7; }
    /* Image tile */
    .hero-tile-image { border-radius: 16px; overflow: hidden; box-shadow: var(--shadow-md); min-height: 180px; background: var(--bg-alt); }
    .hero-tile-image img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .hero-tile-image-placeholder { width: 100%; height: 100%; min-height: 180px; background: var(--golden); display: flex; align-items: center; justify-content: center; }
    .hero-tile-image-placeholder svg { width: 48px; height: 48px; opacity: .5; }
    /* Accent tile */
    .hero-tile-accent { background: var(--lavender); border-radius: 16px; display: flex; align-items: center; justify-content: center; box-shadow: var(--shadow-md); min-height: 80px; position: relative; overflow: hidden; }
    .hero-tile-accent::before { content: ''; position: absolute; width: 120px; height: 120px; border: 4px solid rgba(255,255,255,.25); border-radius: 50%; top: -30px; right: -30px; }
    .hero-tile-accent::after { content: ''; position: absolute; width: 60px; height: 60px; border: 4px solid rgba(255,255,255,.2); border-radius: 50%; bottom: -10px; left: -10px; }
    .hero-tile-accent svg { width: 36px; height: 36px; color: #fff; opacity: .7; }
    /* When image is present, use a 2-row right column; when absent, secondary tiles fill right side */
    .hero-grid-with-image { grid-template-columns: 2fr 1fr; grid-template-rows: 1fr 1fr; }
    .hero-grid-no-image { grid-template-columns: 2fr 1fr; grid-template-rows: 1fr auto; }

    /* SECTIONS */
    .section { padding: 80px 0; }
    .section-light { background: var(--bg); }
    .section-alt { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.8rem, 4vw, 2.6rem); font-weight: 800; margin-bottom: 40px; color: var(--text); }

    /* ABOUT */
    .about-text { font-size: 1.05rem; color: var(--text-muted); max-width: 800px; line-height: 1.9; }

    /* SERVICES MOSAIC */
    .services-mosaic { display: grid; grid-template-columns: repeat(3, 1fr); grid-auto-rows: minmax(180px, auto); gap: 16px; }
    .service-tile { padding: 32px 24px; border-radius: 12px; background: #fff; box-shadow: var(--shadow-sm); border: 1px solid #E7E5E4; transition: transform .3s, box-shadow .3s; }
    .service-tile:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .tile-tall { grid-row: span 2; }
    .tile-teal { border-left: 4px solid var(--teal); }
    .tile-amber { border-left: 4px solid var(--amber); }
    .service-icon { margin-bottom: 12px; }
    .icon-char { font-size: 28px; line-height: 1; }
    .tile-teal .icon-char { color: var(--teal); }
    .tile-amber .icon-char { color: var(--amber); }
    .service-tile h3 { font-family: var(--font-heading); font-size: 1.15rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .service-tile p { color: var(--text-muted); font-size: .9rem; line-height: 1.6; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--primary); }

    /* GALLERY MOSAIC */
    .gallery-mosaic { display: grid; grid-template-columns: repeat(3, 1fr); grid-auto-rows: 200px; gap: 12px; }
    .gallery-wide { grid-column: span 2; }
    .gallery-item img { width: 100%; height: 100%; object-fit: cover; display: block; border-radius: 10px; box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.03); }

    /* TESTIMONIALS */
    .testimonials-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
    .testimonial-card { padding: 28px; background: #fff; border-radius: 12px; box-shadow: var(--shadow-sm); border: 1px solid #E7E5E4; transition: transform .3s; }
    .testimonial-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .testimonial-card.tile-teal { border-top: 3px solid var(--teal); border-left: none; }
    .testimonial-card.tile-amber { border-top: 3px solid var(--amber); border-left: none; }
    .testimonial-body p { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: 1rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #D6D3D1; font-size: 1rem; }
    .star.filled { color: var(--amber); }

    /* FAQ */
    .faq-list { max-width: 800px; }
    .faq-item { border-bottom: 1px solid #E7E5E4; padding: 24px 0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.7; }

    /* PRICE LIST */
    .price-list { max-width: 800px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 24px 0; border-bottom: 1px solid #E7E5E4; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--primary); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 400px; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid #E7E5E4; color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--teal); padding: 16px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: #fff; padding: 20px 0; border-bottom: 1px solid #E7E5E4; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--amber); color: #fff; font-family: var(--font-heading); padding: 8px 20px; font-size: 1rem; font-weight: 700; border-radius: 6px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT */
    .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: start; }
    .contact-info { display: flex; flex-direction: column; gap: 20px; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--primary); flex-shrink: 0; font-size: 20px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; text-transform: uppercase; font-size: .85rem; letter-spacing: 1px; color: var(--primary); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #fff; border: 1px solid #D6D3D1; border-radius: 8px; color: var(--text); padding: 14px 16px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--primary); }
    .contact-form button { padding: 14px; background: var(--teal); color: #fff; font-family: var(--font-heading); font-size: 1rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border: none; border-radius: 8px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(13,148,136,.25); }

    /* FOOTER */
    .footer { background: #1C1917; padding: 40px 0; text-align: center; }
    .footer p { color: #A8A29E; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(250,250,249,.98); border-bottom: 1px solid #E7E5E4; padding: 16px 24px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero-grid, .hero-grid-with-image, .hero-grid-no-image { grid-template-columns: 1fr; grid-template-rows: auto; }
      .hero-tile-main { grid-column: 1; grid-row: auto; padding: 80px 24px 40px; }
      .services-mosaic { grid-template-columns: 1fr; }
      .tile-tall { grid-row: span 1; }
      .gallery-mosaic { grid-template-columns: repeat(2, 1fr); }
      .gallery-wide { grid-column: span 1; }
      .contact-grid { grid-template-columns: 1fr; }
      .testimonials-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 8px; }
    }
    @media (max-width: 480px) {
      .gallery-mosaic { grid-template-columns: 1fr; }
      .hero-grid { padding: 20px 16px 60px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-grid ${heroImage ? 'hero-grid-with-image' : 'hero-grid-no-image'}">
      <div class="hero-tile-main">
        <h1>${safeHeadline}</h1>
        ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
      </div>
      <div class="hero-tile-sub">
        <p>${truncateAtSentence(safeAbout)}</p>
      </div>
      ${heroImage
        ? `<div class="hero-tile-image"><img src="${escapeAttr(heroImage)}" alt="${safeName}"></div>`
        : `<div class="hero-tile-image"><div class="hero-tile-image-placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div></div>`}
      <div class="hero-tile-accent">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
      </div>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
